/**
 * ActionRow.js
 *
 * Context-aware action button row for unified cards.
 *
 * Actions by context:
 * - Discover: Like, Comment, Save, Schedule (schedulable types), View Details
 * - Saved: Like, Comment, Organize, Schedule (schedulable types), Delete, Share (owner only), View Details
 * - Board: Organize, Schedule (schedulable types), Delete, Share, View Details
 */

import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CARD_CONTEXTS, CONTENT_TYPES } from '../../utils/normalizeCardData';
import { sanitizeUrl } from '../../utils/sanitizeUrl';
import Icon from '../shared/Icon';

// People's posts, whose save count is their savers (postSaversSql). Events and
// walking tours carry an unrelated save_count of their own, not shown here.
const POST_TYPES_WITH_SAVE_COUNT = new Set([
  CONTENT_TYPES.TRIP, CONTENT_TYPES.ALBUM, CONTENT_TYPES.ACTIVITY,
  CONTENT_TYPES.COLLECTION, CONTENT_TYPES.BOARD
]);

/**
 * Icon components for action buttons
 */
const Icons = {
  HeartEmpty: () => <Icon name="heart" className="w-5 h-5" />,
  HeartFilled: () => <Icon name="heartFilled" className="w-5 h-5" />,
  Comment: () => <Icon name="megaphone" className="w-5 h-5" />,

  SaveEmpty: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
  ),
  SaveFilled: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
  ),

  Share: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  ),

  Organize: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),

  Schedule: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),

  Delete: () => (
    <Icon name="trash" className="w-5 h-5" />
  ),

  Remove: () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  ),

  ThumbsDown: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
    </svg>
  ),

  Flag: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
    </svg>
  ),

  Block: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  )
};

/**
 * Action button component
 * Accessible with min 44px touch target, focus-visible states
 * Supports overlay variant for use on image backgrounds
 */
const ActionButton = ({
  onClick,
  icon,
  count,
  isActive = false,
  activeColor = 'text-primary',
  hoverColor = 'hover:text-primary hover:bg-primary-lighter',
  title,
  ariaLabel,
  variant = 'default'
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
  };

  const isOverlay = variant === 'overlay';

  // Overlay variant uses white text on dark gradient background
  const baseClasses = isOverlay
    ? 'text-content-inverse/90 hover:text-content-inverse hover:bg-content-inverse/20'
    : `text-content-secondary ${hoverColor}`;

  const activeClasses = isOverlay
    ? (isActive ? 'text-content-inverse' : '')
    : (isActive ? activeColor : '');

  const sizeClasses = isOverlay
    ? 'min-w-[44px] min-h-[44px] px-2'
    : 'min-w-[44px] min-h-[44px] px-2';

  return (
    <button
      onClick={handleClick}
      className={`flex items-center justify-center space-x-1 ${sizeClasses} py-1 rounded-full text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50 focus-visible:ring-offset-1 flex-shrink-0 ${
        isActive ? activeClasses : baseClasses
      }`}
      title={title}
      aria-label={ariaLabel || title}
    >
      {icon}
      {count > 0 && <span className="font-medium text-xs">{count}</span>}
    </button>
  );
};

/**
 * View Details link component
 * Accessible with min touch target and focus-visible states
 * Supports overlay variant for use on image backgrounds
 */
const ViewDetailsLink = ({ detailUrl, type, variant = 'default', typeData }) => {
  const { t } = useTranslation('feedCard');

  const getLinkText = () => {
    switch (type) {
      case CONTENT_TYPES.COLLECTION:
        return t('actions.viewDetailsLink.collection');
      case CONTENT_TYPES.BOARD:
        return t('actions.viewDetailsLink.board');
      case CONTENT_TYPES.WALKING_TOUR:
        return t('actions.viewDetailsLink.walkingTour');
      case CONTENT_TYPES.TRIP:
        return t('actions.viewDetailsLink.trip');
      case CONTENT_TYPES.ALBUM:
        return t('actions.viewDetailsLink.album');
      case CONTENT_TYPES.EVENT_GROUP: {
        const count = typeData?.eventCount;
        return count ? t('actions.viewDetailsLink.eventGroup', { count }) : t('actions.viewDetailsLink.eventGroupFallback');
      }
      case CONTENT_TYPES.ARTIST_EVENT:
      case CONTENT_TYPES.EVENT:
        // Only claim "Tickets" when the event actually has a booking URL.
        // `ticketUrl` collapses booking-url-else-listing-page, so it can't
        // answer this on its own — hasTicketUrl carries the distinction.
        return typeData?.hasTicketUrl
          ? t('actions.viewDetailsLink.tickets')
          : t('actions.viewDetailsLink.moreInfo');
      case CONTENT_TYPES.VIATOR_EXPERIENCE:
        return t('actions.viewDetailsLink.viator');
      default:
        return t('actions.viewDetailsLink.default');
    }
  };

  const isOverlay = variant === 'overlay';
  const colorClasses = isOverlay
    ? 'text-content-inverse/90 hover:text-content-inverse'
    : 'text-primary hover:text-primary-dark';

  const isExternal = detailUrl.startsWith('http');
  const linkClasses = `text-sm font-medium min-h-[44px] flex items-center gap-0.5 px-2 rounded-lg whitespace-nowrap min-w-0 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50 focus-visible:ring-offset-1 ${colorClasses}`;
  // The chevron SeeAllLink draws, out of the string (the "→" used to be typed
  // into every label). The label truncates; the chevron never does.
  const content = (
    <>
      <span className="truncate">{getLinkText()}</span>
      <Icon name="chevronRight" className="w-4 h-4 flex-shrink-0" />
    </>
  );

  if (isExternal) {
    return (
      <a
        href={sanitizeUrl(detailUrl)}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={detailUrl}
      className={linkClasses}
      onClick={(e) => e.stopPropagation()}
    >
      {content}
    </Link>
  );
};

/**
 * Overflow menu (three-dot) with contextual actions.
 * In overlay variant: includes Share, Schedule, Organize, Comment, Archive,
 * Restore, Delete, Remove.
 * Always includes: Not Interested, Report, Block (when handlers provided).
 * Uses a portal to render the dropdown at document body level,
 * escaping overflow-hidden containers on the card.
 */
const OverflowMenu = ({
  onNotInterested, onReport, onBlockUser,
  onShare, onSchedule, onOrganize, onComment, onDelete, onRemove,
  onArchive, onRestore,
  onViewSavedActivities,
  detailUrl, detailLabel, deleteLabel,
  ownerName,
  variant = 'default'
}) => {
  const { t } = useTranslation('feedCard');
  const resolvedDeleteLabel = deleteLabel || t('actions.deleteLabelDefault');
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Position the portal menu relative to the button
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 192; // w-48 = 12rem = 192px
    const gap = 4;

    // Measure actual menu height after render, fallback to estimate
    const menuHeight = menuRef.current?.offsetHeight || 96;

    // Default: open above the button, aligned to right edge
    let top = rect.top - menuHeight - gap;
    let left = rect.right - menuWidth;

    // If not enough room above, open below
    if (top < 8) {
      top = rect.bottom + gap;
    }

    // Keep within viewport horizontally
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }

    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    // Re-position after render so menuRef.offsetHeight is available
    requestAnimationFrame(updatePosition);
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        close();
      }
    };
    const handleEscape = (e) => { if (e.key === 'Escape') close(); };
    const handleScroll = () => close();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, close, updatePosition]);

  const isOverlay = variant === 'overlay';
  const buttonClasses = isOverlay
    ? 'text-content-inverse/90 hover:text-content-inverse hover:bg-content-inverse/20'
    : 'text-content-secondary hover:text-primary hover:bg-primary-lighter';
  const sizeClasses = isOverlay
    ? 'min-w-[36px] min-h-[36px]'
    : 'min-w-[44px] min-h-[44px]';

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        className={`flex items-center justify-center ${sizeClasses} rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50 ${buttonClasses}`}
        title={t('actions.overflowMenu.moreOptionsLabel')}
        aria-label={t('actions.overflowMenu.moreOptionsLabel')}
        aria-expanded={isOpen}
      >
        {/* ⋯ or ⋮, following the device */}
        <Icon name="more" className="w-5 h-5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 bg-surface-dark/95 backdrop-blur-sm rounded-lg shadow-elevation-3 overflow-hidden"
          style={{ top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Contextual actions (overlay variant) */}
          {detailUrl && (
            <a
              href={sanitizeUrl(detailUrl)}
              onClick={(e) => { e.stopPropagation(); close(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icon name="arrowRight" className="w-4 h-4" />
              <span>{detailLabel || t('actions.overflowMenu.viewDetailsFallback')}</span>
            </a>
          )}
          {onViewSavedActivities && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onViewSavedActivities(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icon name="mapPin" className="w-4 h-4" />
              <span>{t('actions.overflowMenu.viewSavedActivities')}</span>
            </button>
          )}
          {onComment && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onComment(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Comment />
              <span>{t('actions.overflowMenu.comment')}</span>
            </button>
          )}
          {onShare && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onShare(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Share />
              <span>{t('actions.overflowMenu.share')}</span>
            </button>
          )}
          {onSchedule && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onSchedule(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Schedule />
              <span>{t('actions.overflowMenu.schedule')}</span>
            </button>
          )}
          {onOrganize && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onOrganize(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Organize />
              <span>{t('actions.overflowMenu.organize')}</span>
            </button>
          )}
          {onRemove && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onRemove(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Remove />
              <span>{t('actions.overflowMenu.remove')}</span>
            </button>
          )}
          {/* Restore sits above Archive so an archived card leads with the way
              back out. Both are non-destructive, hence no error colouring. */}
          {onRestore && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onRestore(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icon name="loopArrow" className="w-4 h-4" />
              <span>{t('actions.overflowMenu.restore')}</span>
            </button>
          )}
          {onArchive && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onArchive(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icon name="archive" className="w-4 h-4" />
              <span>{t('actions.overflowMenu.archive')}</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); onDelete(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-error hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Delete />
              <span>{resolvedDeleteLabel}</span>
            </button>
          )}
          {/* Separator between contextual and moderation actions */}
          {(onNotInterested || onReport || onBlockUser) && (detailUrl || onViewSavedActivities || onComment || onShare || onSchedule || onOrganize || onRemove || onDelete) && (
            <div className="border-t border-content-inverse/20 my-1" />
          )}
          {/* When the card is attributed to a friend traveler but owned by
              someone else, Report/Block target the OWNER — name them here so
              the actions aren't read as targeting the displayed friend. */}
          {ownerName && (onNotInterested || onReport || onBlockUser) && (
            <div className="px-4 pt-2 pb-1 text-xs text-content-inverse/60">
              {t('actions.overflowMenu.aboutTripBy', { ownerName })}
            </div>
          )}
          {onNotInterested && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                close();
                onNotInterested();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.ThumbsDown />
              <span>{t('actions.overflowMenu.notInterested')}</span>
            </button>
          )}
          {onReport && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                close();
                onReport();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-content-inverse/90 hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Flag />
              <span>{t('actions.overflowMenu.report')}</span>
            </button>
          )}
          {onBlockUser && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                close();
                onBlockUser();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-error hover:bg-content-inverse/10 transition-colors"
            >
              <Icons.Block />
              <span>{t('actions.overflowMenu.blockUser')}</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

/**
 * Main ActionRow Component
 *
 * Props:
 * @param {string} type - Content type (activity, collection, etc.)
 * @param {string} context - Display context (discover, saved, board)
 * @param {Object} engagement - { likeCount, commentCount, saveCount, userLiked, userSaved }
 *   saveCount: other people who have the post saved (postSaversSql) — shown,
 *   like likeCount, as a bare number on its button.
 * @param {boolean} isOwner - Whether current user owns this item
 * @param {string} detailUrl - URL for detail page navigation
 * @param {string} variant - 'default' or 'overlay' for image overlay styling
 * @param {Object} actions - Action handler callbacks
 *   @param {Function} actions.onLike - Like button handler
 *   @param {Function} actions.onComment - Comment button handler
 *   @param {Function} actions.onSave - Save button handler
 *   @param {Function} actions.onSchedule - Schedule button handler (activities only)
 *   @param {Function} actions.onOrganize - Organize/manage collections handler
 *   @param {Function} actions.onDelete - Permanent delete handler
 *   @param {Function} actions.onArchive - Archive handler (owned collections/boards)
 *   @param {Function} actions.onRestore - Restore handler (archived items)
 *   @param {Function} actions.onShare - Share handler (owner only)
 *   @param {Function} actions.onRemove - Remove from board handler
 *   @param {Function} actions.onNotInterested - Not interested handler (non-owned content)
 *   @param {Function} actions.onReport - Report handler (non-owned content)
 */
const ActionRow = ({
  type,
  context,
  engagement = {},
  isOwner = false,
  detailUrl,
  variant = 'default',
  actions = {},
  typeData,
  ownerName
}) => {
  const { t } = useTranslation('feedCard');
  const {
    likeCount = 0,
    commentCount = 0,
    saveCount: rawSaveCount = 0,
    userLiked = false,
    userSaved = false
  } = engagement;
  const saveCount = POST_TYPES_WITH_SAVE_COUNT.has(type) ? rawSaveCount : 0;

  const {
    onLike,
    onComment,
    onSave,
    onSchedule,
    onOrganize,
    onArchive,
    onRestore,
    onDelete,
    onShare,
    onRemove,
    onViewSavedActivities,
    onNotInterested,
    onReport,
    onBlockUser,
    deleteLabel = t('actions.deleteLabelDefault')
  } = actions;

  const isSchedulable = [
    CONTENT_TYPES.ACTIVITY, CONTENT_TYPES.ARTIST_EVENT, CONTENT_TYPES.EVENT,
    CONTENT_TYPES.WALKING_TOUR, CONTENT_TYPES.TOURISM_EXPERIENCE, CONTENT_TYPES.VIATOR_EXPERIENCE
  ].includes(type);

  // Single events split their two destinations: the CARD click goes into the
  // app (venue page), while this right-slot link is the one-click external
  // "Tickets" path. Deriving it from detailUrl would point "Tickets" at the
  // venue page for every venue-backed event. No ticket URL → no link.
  const viewDetailsUrl = (type === CONTENT_TYPES.EVENT || type === CONTENT_TYPES.ARTIST_EVENT)
    ? (typeData?.ticketUrl || null)
    : detailUrl;
  const isDiscover = context === CARD_CONTEXTS.DISCOVER;
  const isSaved = context === CARD_CONTEXTS.SAVED;
  const isBoard = context === CARD_CONTEXTS.BOARD;
  const isOverlay = variant === 'overlay';

  // Container classes differ based on variant
  const containerClasses = isOverlay
    ? 'flex items-center justify-between'
    : 'flex items-center justify-between pt-2 border-t border-border mt-auto';

  // In overlay mode: only Heart + Save are inline; everything else goes to the overflow menu
  if (isOverlay) {
    // Determine which contextual actions belong in the overflow menu
    const overlaySchedule = isSchedulable && (isDiscover || isSaved || isBoard) && onSchedule ? onSchedule : undefined;
    const overlayOrganize = (isSaved || isBoard) && onOrganize ? onOrganize : undefined;
    const overlayDelete = isSaved && onDelete ? onDelete : undefined;
    // Archive/Restore are Saved-context only — they are how the Saved page and
    // its archive move owned collections and boards between the two.
    const overlayArchive = isSaved && onArchive ? onArchive : undefined;
    const overlayRestore = isSaved && onRestore ? onRestore : undefined;

    const hasMenuActions = onViewSavedActivities || onComment || onShare || overlaySchedule || overlayOrganize || overlayArchive || overlayRestore || overlayDelete || onRemove || onNotInterested || onReport || onBlockUser;

    return (
      <div className={containerClasses}>
        <div className="flex items-center space-x-0 flex-shrink-0">
          {/* Like button */}
          {onLike && (
            <ActionButton
              onClick={onLike}
              icon={userLiked ? <Icons.HeartFilled /> : <Icons.HeartEmpty />}
              count={likeCount}
              isActive={userLiked}
              activeColor="text-error"
              hoverColor="hover:text-error hover:bg-error-light"
              title={userLiked ? t('actions.like.titleLiked') : t('actions.like.titleUnliked')}
              variant={variant}
            />
          )}

          {/* Save button */}
          {isDiscover && onSave && (
            <ActionButton
              onClick={onSave}
              icon={userSaved ? <Icons.SaveFilled /> : <Icons.SaveEmpty />}
              count={saveCount}
              isActive={userSaved}
              title={userSaved ? t('actions.save.titleSaved') : t('actions.save.titleUnsaved')}
              variant={variant}
            />
          )}
        </div>

        <div className="flex items-center flex-shrink-0">
          {viewDetailsUrl && (
            <ViewDetailsLink detailUrl={viewDetailsUrl} type={type} variant={variant} typeData={typeData} />
          )}
          {hasMenuActions && (
            <OverflowMenu
              onViewSavedActivities={onViewSavedActivities}
              onComment={onComment}
              onShare={onShare}
              onSchedule={overlaySchedule}
              onOrganize={overlayOrganize}
              onArchive={overlayArchive}
              onRestore={overlayRestore}
              onDelete={overlayDelete}
              deleteLabel={deleteLabel}
              onRemove={onRemove}
              onNotInterested={onNotInterested}
              onReport={onReport}
              onBlockUser={onBlockUser}
              ownerName={ownerName}
              variant={variant}
            />
          )}
        </div>
      </div>
    );
  }

  // Default (non-overlay) variant — unchanged, all buttons inline
  return (
    <div className={containerClasses}>
      <div className="flex items-center space-x-0 flex-shrink-0">
        {/* Like button - shown when handler provided */}
        {onLike && (
          <ActionButton
            onClick={onLike}
            icon={userLiked ? <Icons.HeartFilled /> : <Icons.HeartEmpty />}
            count={likeCount}
            isActive={userLiked}
            activeColor="text-error"
            hoverColor="hover:text-error hover:bg-error-light"
            title={userLiked ? t('actions.like.titleLiked') : t('actions.like.titleUnliked')}
            variant={variant}
          />
        )}

        {/* Comment button - shown when handler provided */}
        {onComment && (
          <ActionButton
            onClick={onComment}
            icon={<Icons.Comment />}
            count={commentCount}
            title={t('actions.comment.title')}
            variant={variant}
          />
        )}

        {/* Save button - Discover context */}
        {isDiscover && onSave && (
          <ActionButton
            onClick={onSave}
            icon={userSaved ? <Icons.SaveFilled /> : <Icons.SaveEmpty />}
            count={saveCount}
            isActive={userSaved}
            title={userSaved ? t('actions.save.titleSaved') : t('actions.save.titleUnsaved')}
            variant={variant}
          />
        )}

        {/* Schedule button - Schedulable types in Discover, Saved, and Board contexts */}
        {isSchedulable && (isDiscover || isSaved || isBoard) && onSchedule && (
          <ActionButton
            onClick={onSchedule}
            icon={<Icons.Schedule />}
            title={t('actions.schedule.title')}
            variant={variant}
          />
        )}

        {/* Organize button - Saved and Board contexts */}
        {(isSaved || isBoard) && onOrganize && (
          <ActionButton
            onClick={onOrganize}
            icon={<Icons.Organize />}
            title={t('actions.organize.title')}
            variant={variant}
          />
        )}

        {/* View saved activities - trip cards in Saved context */}
        {isSaved && onViewSavedActivities && (
          <ActionButton
            onClick={onViewSavedActivities}
            icon={<Icon name="mapPin" className="w-5 h-5" />}
            title={t('actions.viewSavedActivities.title')}
            variant={variant}
          />
        )}

        {/* Delete button - Saved context only (global unsave or owner delete) */}
        {isSaved && onDelete && (
          <ActionButton
            onClick={onDelete}
            icon={<Icons.Delete />}
            hoverColor="hover:text-error hover:bg-error-light"
            title={deleteLabel === 'Delete' ? t('actions.deleteButton.tourTitle') : t('actions.deleteButton.savedTitle')}
            variant={variant}
          />
        )}

        {/* Remove button - Board/Collection contexts (remove from container only) */}
        {onRemove && (
          <ActionButton
            onClick={onRemove}
            icon={<Icons.Remove />}
            hoverColor="hover:text-error hover:bg-error-light"
            title={t('actions.remove.title')}
            variant={variant}
          />
        )}

        {/* Share button - shown when handler provided (caller gates by ownership) */}
        {onShare && (
          <ActionButton
            onClick={onShare}
            icon={<Icons.Share />}
            title={t('actions.share.title')}
            variant={variant}
          />
        )}

      </div>

      <div className="flex items-center flex-shrink-0">
        {/* View Details link */}
        {viewDetailsUrl && (
          <ViewDetailsLink detailUrl={viewDetailsUrl} type={type} variant={variant} typeData={typeData} />
        )}

        {/* Overflow menu - Not Interested / Report / Block */}
        {(onNotInterested || onReport || onBlockUser) && (
          <OverflowMenu
            onNotInterested={onNotInterested}
            onReport={onReport}
            onBlockUser={onBlockUser}
            ownerName={ownerName}
            variant={variant}
          />
        )}
      </div>
    </div>
  );
};

export default memo(ActionRow);
